import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { createMemberRecord, fetchMembers, Member, resendMemberCard } from '../services/apiClient';

type MemberStatusFilter = 'all' | 'Paid' | 'Pending Payment' | 'Inactive';
type MemberTierFilter = 'all' | 'Individual' | 'Couple' | 'Family' | 'VIP';

const DEFAULT_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  tier: 'Individual' as const,
  status: 'Pending Payment' as const,
};

export const MembersList: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>('all');
  const [tierFilter, setTierFilter] = useState<MemberTierFilter>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [resendMemberId, setResendMemberId] = useState<string | null>(null);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchMembers(8);
        setMembers(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load members';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, []);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members.filter((member) => {
      const matchesName =
        !query ||
        member.fullName.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
      const matchesTier = tierFilter === 'all' || member.tier === tierFilter;
      return matchesName && matchesStatus && matchesTier;
    });
  }, [members, search, statusFilter, tierFilter]);

  const onCreateMember = async (event: FormEvent) => {
    event.preventDefault();
    setFormMessage(null);
    setActionMessage(null);

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setFormMessage('First name, last name, and email are required.');
      return;
    }

    try {
      setSubmitting(true);
      const created = await createMemberRecord({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        tier: form.tier,
        status: form.status,
      });

      setMembers((prev) => [created, ...prev]);
      setForm(DEFAULT_FORM);
      setFormMessage('Member added successfully.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add member.';
      setFormMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const onResendCard = async (member: Member) => {
    setActionMessage(null);
    try {
      setResendMemberId(member.id);
      await resendMemberCard(member.id);
      setActionMessage(`Digital card resend queued for ${member.fullName}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend digital card.';
      setActionMessage(message);
    } finally {
      setResendMemberId(null);
    }
  };

  if (loading) {
    return (
      <div className="table-wrap">
        <div className="table-header">
          <p className="table-title">Member Directory</p>
        </div>
        <p className="status-loading">Loading members...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-wrap">
        <div className="table-header">
          <p className="table-title">Member Directory</p>
        </div>
        <p className="status-error">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <div className="table-header">
        <p className="table-title">Member Directory</p>
        <button type="button" className="primary-action" onClick={() => setShowAddForm((v) => !v)}>
          {showAddForm ? 'Close Add Member' : 'Add Member'}
        </button>
      </div>

      {showAddForm && (
        <form className="member-form" onSubmit={onCreateMember}>
          <div className="member-form-grid">
            <label>
              First Name
              <input
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                placeholder="First name"
              />
            </label>
            <label>
              Last Name
              <input
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                placeholder="Last name"
              />
            </label>
            <label>
              Email Address
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="member@email.com"
              />
            </label>
            <label>
              Membership Level
              <select
                value={form.tier}
                onChange={(e) => setForm((prev) => ({ ...prev, tier: e.target.value as typeof form.tier }))}
              >
                <option value="Individual">Individual</option>
                <option value="Couple">Couple</option>
                <option value="Family">Family</option>
                <option value="VIP">VIP</option>
              </select>
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as typeof form.status }))}
              >
                <option value="Paid">Paid</option>
                <option value="Pending Payment">Pending Payment</option>
              </select>
            </label>
          </div>
          <div className="member-form-actions">
            <button type="submit" className="primary-action" disabled={submitting}>
              {submitting ? 'Adding...' : 'Save Member'}
            </button>
            {formMessage && <p className={formMessage.includes('successfully') ? 'status-loading' : 'status-error'}>{formMessage}</p>}
          </div>
        </form>
      )}

      <div className="member-filters">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as MemberStatusFilter)}
        >
          <option value="all">All Statuses</option>
          <option value="Paid">Paid</option>
          <option value="Pending Payment">Pending Payment</option>
          <option value="Inactive">Inactive</option>
        </select>
        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value as MemberTierFilter)}>
          <option value="all">All Levels</option>
          <option value="Individual">Individual</option>
          <option value="Couple">Couple</option>
          <option value="Family">Family</option>
          <option value="VIP">VIP</option>
        </select>
      </div>

      {actionMessage && <p className="status-loading">{actionMessage}</p>}

      {filteredMembers.length === 0 ? (
        <p className="empty-state">No members found</p>
      ) : (
        <table className="list-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Level</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member) => (
              <tr key={member.id}>
                <td>{member.firstName || 'Unknown'}</td>
                <td>{member.lastName || 'Unknown'}</td>
                <td className="email-cell">{member.email || 'No email'}</td>
                <td>
                  <span className={`tier-pill tier-${member.tier || 'Individual'}`}>{member.tier || 'Individual'}</span>
                </td>
                <td>
                  <span className={`status-badge status-${member.status.toLowerCase().replace(/\s+/g, '-')}`}>
                    {member.status}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={() => onResendCard(member)}
                    disabled={resendMemberId === member.id}
                  >
                    {resendMemberId === member.id ? 'Sending...' : 'Resend Card'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
